import { defineStore } from "pinia";
import { ref } from "vue";
import { publicCatalogRepository, bookingIntentRepository } from "../repositories";
import { CapabilityRequestError } from "../services";
// Statuses that mean the intent/child grant is dead and a fresh intent is
// required (400 invalid proposal, 404 expired/mismatched grant, 409 capacity
// conflict). 429/503 are transient — the in-memory intent/proposal survive so
// the caller can retry without losing the fingerprint.
const TERMINAL_INTENT_STATUSES = new Set([400, 404, 409]);
function extractErrorMessage(err) {
    if (err instanceof CapabilityRequestError) {
        const body = err.responseBody;
        return body?.message ?? `Request failed with status ${err.status}`;
    }
    return err instanceof Error ? err.message : "Unexpected error";
}
function parseRetryAfterSeconds(retryAfter) {
    if (!retryAfter)
        return null;
    const seconds = Number(retryAfter);
    return Number.isFinite(seconds) ? seconds : null;
}
export const useBookingWidgetStore = defineStore("booking-widget", () => {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    // In-memory only — never persisted to localStorage/sessionStorage per the
    // access-grant contract.
    const config = ref(null);
    const catalog = ref(null);
    const availability = ref(null);
    const reviews = ref(null);
    // The immutable fingerprint tuple: set together by startIntent(), cleared
    // together on terminal failure or successful booking.
    const lockedProposal = ref(null);
    const intent = ref(null);
    const bookingResult = ref(null);
    const isLoading = ref(false);
    const isIntentInFlight = ref(false);
    const isConfirming = ref(false);
    const error = ref(null);
    const lastErrorStatus = ref(null);
    const retryAfterSeconds = ref(null);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    function configure(next) {
        config.value = next;
    }
    function requireConfig() {
        if (!config.value)
            throw new Error("Booking widget is not configured");
        return config.value;
    }
    async function loadCatalog() {
        const { apiBaseUrl, organizationId, catalogGrant } = requireConfig();
        isLoading.value = true;
        error.value = null;
        try {
            catalog.value = await publicCatalogRepository.getCatalog(apiBaseUrl, organizationId, catalogGrant);
        }
        catch (err) {
            error.value = extractErrorMessage(err);
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function loadAvailability(serviceId, params) {
        const { apiBaseUrl, organizationId, catalogGrant } = requireConfig();
        isLoading.value = true;
        error.value = null;
        try {
            availability.value = await publicCatalogRepository.getAvailability(apiBaseUrl, organizationId, serviceId, catalogGrant, params);
        }
        catch (err) {
            error.value = extractErrorMessage(err);
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function loadReviews(serviceId, params) {
        const { apiBaseUrl, organizationId, catalogGrant } = requireConfig();
        isLoading.value = true;
        error.value = null;
        try {
            reviews.value = await publicCatalogRepository.getReviews(apiBaseUrl, organizationId, serviceId, catalogGrant, params);
        }
        catch (err) {
            error.value = extractErrorMessage(err);
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function startIntent(proposal, captchaToken) {
        if (isIntentInFlight.value)
            throw new Error("A booking intent request is already in flight");
        const { apiBaseUrl, organizationId, catalogGrant } = requireConfig();
        isIntentInFlight.value = true;
        error.value = null;
        lastErrorStatus.value = null;
        retryAfterSeconds.value = null;
        try {
            const created = await bookingIntentRepository.createIntent(apiBaseUrl, organizationId, catalogGrant, proposal, captchaToken);
            lockedProposal.value = proposal;
            intent.value = created;
            return created;
        }
        catch (err) {
            error.value = extractErrorMessage(err);
            if (err instanceof CapabilityRequestError) {
                lastErrorStatus.value = err.status;
                retryAfterSeconds.value = err.status === 429 ? parseRetryAfterSeconds(err.retryAfter) : null;
                if (TERMINAL_INTENT_STATUSES.has(err.status)) {
                    lockedProposal.value = null;
                    intent.value = null;
                }
            }
            throw err;
        }
        finally {
            isIntentInFlight.value = false;
        }
    }
    async function confirmBooking() {
        if (!intent.value || !lockedProposal.value) {
            throw new Error("No active booking intent to confirm");
        }
        const { apiBaseUrl } = requireConfig();
        const activeIntent = intent.value;
        const activeProposal = lockedProposal.value;
        isConfirming.value = true;
        error.value = null;
        lastErrorStatus.value = null;
        retryAfterSeconds.value = null;
        try {
            const result = await bookingIntentRepository.createBooking(apiBaseUrl, activeIntent.intentId, activeIntent.accessGrant, activeProposal);
            bookingResult.value = result;
            lockedProposal.value = null;
            intent.value = null;
            return result;
        }
        catch (err) {
            error.value = extractErrorMessage(err);
            if (err instanceof CapabilityRequestError) {
                lastErrorStatus.value = err.status;
                retryAfterSeconds.value = err.status === 429 ? parseRetryAfterSeconds(err.retryAfter) : null;
                if (TERMINAL_INTENT_STATUSES.has(err.status)) {
                    lockedProposal.value = null;
                    intent.value = null;
                }
                // 503: intent/proposal deliberately left intact so the caller can
                // retry within the child grant's expiry window.
            }
            throw err;
        }
        finally {
            isConfirming.value = false;
        }
    }
    function clearIntent() {
        lockedProposal.value = null;
        intent.value = null;
        lastErrorStatus.value = null;
        retryAfterSeconds.value = null;
    }
    function $reset() {
        config.value = null;
        catalog.value = null;
        availability.value = null;
        reviews.value = null;
        lockedProposal.value = null;
        intent.value = null;
        bookingResult.value = null;
        isLoading.value = false;
        isIntentInFlight.value = false;
        isConfirming.value = false;
        error.value = null;
        lastErrorStatus.value = null;
        retryAfterSeconds.value = null;
    }
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        config,
        catalog,
        availability,
        reviews,
        lockedProposal,
        intent,
        bookingResult,
        isLoading,
        isIntentInFlight,
        isConfirming,
        error,
        lastErrorStatus,
        retryAfterSeconds,
        configure,
        loadCatalog,
        loadAvailability,
        loadReviews,
        startIntent,
        confirmBooking,
        clearIntent,
        $reset,
    };
});
